import { EventEmitter, OnInit } from '@angular/core';

import { Observable, Subscription, throwError } from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import { ListErrorType, ListFilterOperator } from '../../enums';
import { ListParameters } from '../../interfaces';
import { ListObject } from '../../models';
import { CoreService } from '../../services/core.service';
import { PaginatableComponent } from '../paginatable/paginatable.component';

/**
 * This is the abstract main component base of the list components.
 * It has the most features, that is necessary for a listing component.
 *
 * @export
 * @abstract
 * @class ListComponent
 * @extends {BaseComponent}
 * @template T Type of the selected list objects.
 */
export abstract class ListComponent<T> extends PaginatableComponent implements OnInit {
  /**
   * Public getter of the error state.
   *
   * @readonly
   * @type {boolean}
   * @memberof ListComponent
   */
  public get hasError(): boolean {
    return this.error;
  }

  /**
   * This getter gives the object, that contains: Filters, orders, searchtext, paginator datas
   * for the API call. This can be used as query data.
   *
   * @readonly
   * @protected
   * @type {object}
   * @memberof ListComponent
   */
  protected get parameters(): ListParameters {
    const resp: ListParameters = {
      page: this.paginateObject.currentPage,
      size: this.paginateObject.itemsPerPage,
      orders: undefined,
      filters: [],
      search: this.searchText,
    };

    // Adding the orders to the response object.
    if (this.orders) {
      resp.orders = this.orders.map((order) => `${order.reverse ? '-' : ''}${order.key}`).join(',');
    } else {
      // Or deletes it, if there are no orders.
      delete resp.orders;
    }

    // Adding the filters to the response object.
    if (this.filters) {
      resp.filters = this.filters.map(
        (filter) => `${filter.name || filter.key}${filter.operator || ListFilterOperator.equalTo}${filter.value}`
      );
    } else {
      // Or deletes it, if there are no filters.
      delete resp.filters;
    }

    return resp;
  }
  /**
   * This variable stores the array of T instances.
   *
   * @memberof ListComponent
   * @type T[]
   * @default null
   */
  public list?: T[] = null;

  /**
   * This property stores the type of the previous error.
   *
   * @type {ListErrorType}
   * @memberof ListComponent
   */
  public errorType: ListErrorType;

  /**
   * This property represents the searching text.
   *
   * @type {string}
   * @memberof ListComponent
   */
  public searchText: string;

  /**
   * This event fires, when the list is loaded.
   *
   * @type {EventEmitter<void>}
   * @memberof ListComponent
   */
  public onListLoaded: EventEmitter<void> = new EventEmitter<void>();

  /**
   * This subscription stores the loding observable of the list.
   *
   * @protected
   * @type {Subscription}
   * @memberof ListComponent
   */
  protected subscription: Subscription;

  /**
   * This variable shows if there is any error happend lately.
   *
   * @memberof ListComponent
   * @type boolean
   * @default false
   */
  private error = false;

  /**
   * Creates an instance of ListComponent.
   * @memberof ListComponent
   */
  constructor(protected readonly coreService: CoreService) {
    super();

    // Subscribe for the refresh list event.
    if (this.coreService) {
      this.destroyableSubscriptions.push(this.coreService.refreshListView.subscribe(() => this.loadList()));
    }

    // Filter or Order changed event
    this.destroyableSubscriptions.push(this.filterChanged.subscribe(() => this.loadList()));

    // Pagination changed event
    this.destroyableSubscriptions.push(this.paginationChanged.subscribe(() => this.loadList()));
  }

  /**
   * Sets the searchText
   *
   * @param {string} value
   * @memberof ListComponent
   */
  public setSearchText(value: string) {
    this.searchText = value;
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * Define an ngOnInit() method to handle any additional initialization tasks.
   *
   * @memberof ListComponent
   */
  public ngOnInit() {
    if (this.onListChanged) {
      this.destroyableSubscriptions.push(
        this.onListChanged().subscribe(
          (response) => this.setList(response),
          (err) => throwError(this.errorHandler(err, ListErrorType.FailedToRefreshList))
        )
      );
    }

    this.loadList();
  }

  /**
   * This function reloads the list content. This can be called from the search input.
   *
   * @memberof ListComponent
   */
  public onChange() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      delete this.subscription;
    }

    this.loadList();
  }

  /**
   * This is the must have function of this component. This contains the main source of the model list.
   *
   * @protected
   * @abstract
   * @returns {Observable<ListObject<T>>}
   * @memberof ListComponent
   */
  protected abstract getList(): Observable<ListObject<T> | T[]>;

  /**
   * This function loads the list from the API call.
   *
   * @protected
   * @memberof ListComponent
   */
  protected loadList() {
    this.startLoading();
    this.subscription = this.getList().subscribe(
      (response) => this.setList(response),
      (err) => throwError(this.errorHandler(err, ListErrorType.FailedToLoadList))
    );
  }

  /**
   * This function fills the list property and stops the loader.
   *
   * @private
   * @param {ListObject<T>} response
   * @memberof ListComponent
   */
  private setList(response: ListObject<T> | T[]) {
    if (response instanceof Array) {
      this.list = response;
    } else {
      this.list = response.data;
      this.paginateObject.totalItems = response.count;
    }
    this.onListLoaded.emit();

    if (this.afterListLoaded) {
      this.afterListLoaded();
    }

    this.stopLoading();
    delete this.subscription;
  }

  /**
   * Simple error handler.
   *
   * @private
   * @param {any} error Error
   * @param {ListErrorType} errorType Type of error
   * @memberof ListComponent
   */
  private errorHandler(error: HttpErrorResponse, errorType: ListErrorType): Observable<T> {
    console.error(error);
    this.stopLoading();
    this.error = true;
    this.errorType = errorType;
    delete this.subscription;

    const unAuthorized = 401;
    if (error.status === unAuthorized) {
      location.reload();
    }

    return new Observable<T>();
  }
}

/**
 * This interface contains the optional functions of the ListComponent<T>
 *
 * @export
 * @interface ListComponent
 * @template T
 */
export interface ListComponent<T> {
  // Optional methods

  /**
   * @returns {EventEmitter<ListObject<T>>}
   * @memberof ListComponent
   */
  onListChanged?(): EventEmitter<ListObject<T>>;

  /**
   * This function calls, when the list is loaded.
   *
   * @memberof ListComponent
   */
  afterListLoaded?(): void;
}
